const color = 'aqua'
const keypointColor = 'lime'
const boundingBoxColor = 'blue'
const lineWidth = 2

// compute some metadata given a landmark index
// - is the landmark a palm keypoint or a finger keypoint?
// - what's the next landmark to connect to if we're drawing a bone?
function getLandmarkProperty(i) {
  var palms = [0, 1, 2, 5, 9, 13, 17] //landmark indices that represent the palm

  var idx = palms.indexOf(i)
  var isPalm = idx !== -1
  var next // who to connect with?
  if (!isPalm) {
    // connect with previous finger landmark if it's a finger landmark
    next = i - 1
  } else {
    // connect with next palm landmark if it's a palm landmark
    next = palms[(idx + 1) % palms.length]
  }
  return { isPalm, next }
}

// draw a hand object (2D debug view) returned by handpose
export function drawHands(predictions, ctx, minConfidence = 0.7, scale = 1) {
  /*
    `predictions` is an array of objects describing each detected hand, for example:
    [
      {
        handInViewConfidence: 1, // The probability of a hand being present.
        boundingBox: { // The bounding box surrounding the hand.
          topLeft: [162.91, -17.42],
          bottomRight: [548.56, 368.23],
        },
        landmarks: [ // The 3D coordinates of each hand landmark.
          [472.52, 298.59, 0.00],
          [412.80, 315.64, -6.18],
          ...
        ],
        annotations: { // Semantic groupings of the `landmarks` coordinates.
          thumb: [
            [412.80, 315.64, -6.18]
            [350.02, 298.38, -7.14],
            ...
          ],
          ...
        }
      }
    ]
    */
  // Each hand object contains a `landmarks` property,
  // which is an array of 21 3-D landmarks.
  for (var i = 0; i < predictions.length; i++) {
    if (predictions[i].handInViewConfidence < minConfidence) continue

    var landmarks = predictions[i].landmarks

    for (var j = 0; j < landmarks.length; j++) {
      // draw the skeleton
      var { next } = getLandmarkProperty(j)

      const [initialX, initialY] = landmarks[j]
      const [nextX, nextY] = landmarks[next]

      drawSegment([initialY, initialX], [nextY, nextX], color, scale, ctx)
    }
  }
}

export function drawTargets(imgRef, targets, ctx) {
  targets.forEach((target) => {
    const { type, coordinates } = target
    const [minX, minY, maxX, maxY] = coordinates
    const height = maxY - minY
    const width = maxX - minX
    // Render as square.
    const dim = Math.max(height, width)
    ctx.drawImage(imgRef[type], minX, minY, dim, dim)
  })
}

export function drawPoint(ctx, y, x, r, color) {
  ctx.beginPath()
  ctx.arc(x, y, r, 0, 2 * Math.PI)
  ctx.fillStyle = color
  ctx.fill()
}

/**
 * Draws a line on a canvas, i.e. a joint
 */
export function drawSegment([ay, ax], [by, bx], color, scale, ctx) {
  ctx.beginPath()
  ctx.moveTo(ax * scale, ay * scale)
  ctx.lineTo(bx * scale, by * scale)
  ctx.lineWidth = lineWidth
  ctx.strokeStyle = color
  ctx.stroke()
}

/**
 * Draw hand keypoints onto a canvas
 */
export function drawKeypoints(
  predictions,
  ctx,
  minConfidence = 0.7,
  scale = 1
) {
  for (let i = 0; i < predictions.length; i++) {
    if (predictions[i].handInViewConfidence < minConfidence) continue
    const keypoints = predictions[i].landmarks

    keypoints.forEach((keypoint) => {
      const [x, y] = keypoint
      drawPoint(ctx, y * scale, x * scale, 3, keypointColor)
    })
  }
}

/**
 * Draw the bounding box of a hand.
 */
export function drawBoundingBox(predictions, ctx, minConfidence = 0.7) {
  for (var i = 0; i < predictions.length; i++) {
    if (predictions[i].handInViewConfidence < minConfidence) continue
    const hand = predictions[i]
    const boundingBox = hand.boundingBox

    const minX = boundingBox.bottomRight[0]
    const maxY = boundingBox.bottomRight[1]
    const maxX = boundingBox.topLeft[0]
    const minY = boundingBox.topLeft[1]

    ctx.rect(minX, minY, maxX - minX, maxY - minY)

    ctx.strokeStyle = boundingBoxColor
    ctx.stroke()
  }
}

/**
 * Converts an arary of pixel data into an ImageData object
 */
export async function renderToCanvas(a, ctx) {
  const [height, width] = a.shape
  const imageData = new ImageData(width, height)

  const data = await a.data()

  for (let i = 0; i < height * width; ++i) {
    const j = i * 4
    const k = i * 3

    imageData.data[j + 0] = data[k + 0]
    imageData.data[j + 1] = data[k + 1]
    imageData.data[j + 2] = data[k + 2]
    imageData.data[j + 3] = 255
  }

  ctx.putImageData(imageData, 0, 0)
}

/**
 * Draw an image on a canvas
 */
export function renderImageToCanvas(image, size, canvas) {
  canvas.width = size[0]
  canvas.height = size[1]
  const ctx = canvas.getContext('2d')

  ctx.drawImage(image, 0, 0)
}

export function drawBoxCoords(predictions, ctx) {
  for (var i = 0; i < predictions.length; i++) {
    const box = predictions[i]
    ctx.rect(box[0], box[1], box[2] - box[0], box[3] - box[1])
    ctx.strokeStyle = boundingBoxColor
    ctx.stroke()
  }
}
